import { pub, sub } from '../utils/pub-sub'
import SysButton from '../widgets/sys-button'
import SysPsvButton from '../widgets/sys-psv-button'
import Widget from '../widgets/widget'
import isDefined from './is-defined'
import logger from './logger'
import generateUuid from './uuid'
import widgetConfigFromId from './widget-config'

/**
 * Helper class to instantiate and load widgets and their respective HTML iframe elements.
 *
 * @param {Window} window A reference to the main frame's window object.
 */
export function WidgetLoader(window) {
  this.widgets = {
    Widget,
    SysButton,
    SysPsvButton,
  }
  this.window = window
  this.widgetInstances = {}
  this.widgetContainers = {}

  if (this.window.MutationObserver) {
    const nodeRemovedMutation = function(removedNode) {
      const widgetUuid = removedNode.getAttribute('data-8select-widget-uuid')
      if (!widgetUuid) {
        return
      }

      if (this.widgetInstances[widgetUuid] === undefined) {
        return
      }

      const messageParameters = { widgetId: widgetUuid, widgetType: this.widgetInstances[widgetUuid].type }

      pub('widget:removedFromDom', messageParameters)
    }.bind(this)

    this.removedObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.removedNodes.forEach(nodeRemovedMutation)
      })
    })

    this.updatedObserver = new MutationObserver(
      function(mutations) {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && !!mutation.target.getAttribute(mutation.attributeName)) {
            const context = {
              attribute: mutation.attributeName,
              oldValue: mutation.oldValue,
              newValue: mutation.target.getAttribute(mutation.attributeName),
            }

            logger('widget-node attribute updated', context)

            if (mutation.oldValue === mutation.target.getAttribute(mutation.attributeName)) {
              logger('value did not change - doing nothing')

              return
            }

            this.destroy(mutation.target.getAttribute('data-8select-widget-uuid'))
            this.createWidget(mutation.target)
          }
        }
      }.bind(this)
    )
  } else {
    logger('MutationObserver not available - falling back to default behavior.')
  }

  sub('widget:destroy', 'widget-loader', context => {
    if (!context || !context.widgetId) {
      return
    }
    const widgetUuid = context.widgetId
    const containerUuid =
      this.widgetInstances[context.widgetId].container &&
      this.widgetInstances[context.widgetId].container.getAttribute('data-8select-widget-container-uuid')

    delete this.widgetInstances[widgetUuid]
    if (containerUuid) {
      delete this.widgetContainers[containerUuid].widgetInstances[widgetUuid]

      const hasAnyVisibleWidget = Object.values(this.widgetInstances).some(
        widget => widget.node && widget.node.style !== 'none'
      )
      if (
        hasAnyVisibleWidget === false ||
        Object.keys(this.widgetContainers[containerUuid].widgetInstances).length === 0
      ) {
        this.widgetContainers[containerUuid].node.style.display = 'none'
      }
    }
  })
}

WidgetLoader.prototype.getAllWidgetInstances = function(widgetUuid) {
  var self = this
  return Object.keys(this.widgetInstances).map(function(widgetUuid) {
    return self.find(widgetUuid)
  })
}

WidgetLoader.prototype.isWidgetClassAvailable = function(classname, widgetId) {
  return !!this.widgets[classname]
}

function isWidgetDisabled(classname, widgetId) {
  return (
    isDefined(window._8select.config.widgets) &&
    isDefined(window._8select.config.widgets[classname]) &&
    window._8select.config.widgets[classname].disabled === true
  )
}

WidgetLoader.prototype.createWidget = function(node) {
  if (this.isDiscovered(node)) {
    logger('Widget already created', node)
    return
  }

  if (typeof window._8select.config.hooks.beforeCreateWidget === 'function') {
    node = window._8select.config.hooks.beforeCreateWidget(node)
    if (!node) {
      return
    }
  }

  node.setAttribute('data-8select-widget-state', 'discovered')

  const widgetId = node.getAttribute('data-8select-widget-id')
  const widgetConfig = widgetConfigFromId(widgetId)

  if (widgetConfig.error) {
    logger('Widget config error', { widgetConfig, error: widgetConfig.error })

    return
  }

  const uuid = generateUuid()
  pub('sdk:createWidget:start', { node, uuid, widgetId, widgetConfig })
  node.setAttribute('data-8select-widget-uuid', uuid)

  if (!this.isWidgetClassAvailable(widgetConfig.constructor, widgetId)) {
    console.error(
      `8SELECT: Widget with data-8select-widget-id="${widgetId}" is not available.
      Please check https://docs.8select.io for available widget types.`
    )

    return
  }

  if (isWidgetDisabled(widgetConfig.config.type, widgetId)) {
    console.debug(`8SELECT: Widget with data-8select-widget-id="${widgetId}" is disabled via SDK config.`)

    return
  }

  const container = this.findContainer(node)
  const hasContainer = container !== undefined
  let containerUuid = generateUuid()
  if (hasContainer) {
    if (container.hasAttribute('data-8select-widget-container-uuid')) {
      containerUuid = container.getAttribute('data-8select-widget-container-uuid')
    } else {
      container.setAttribute('data-8select-widget-container-uuid', containerUuid)
      this.widgetContainers[containerUuid] = { node: container, widgetInstances: {} }
    }
  }
  this.widgetInstances = this.widgetInstances || {}
  this.widgetInstances[uuid] = new this.widgets[widgetConfig.constructor]({
    id: uuid,
    node,
    container,
    window: this.window,
    config: widgetConfig.config,
  })
  this.widgetInstances[uuid].load(() => this.window.eightlytics('loadWidget', {}, undefined, uuid))

  if (hasContainer) {
    this.widgetContainers[containerUuid].widgetInstances[uuid] = this.widgetInstances[uuid]
  }

  if (!window.MutationObserver) {
    return
  }

  this.removedObserver.observe(node.parentNode, {
    subtree: false,
    childList: true,
  })
  if (this.window._8select.config.featureFlags.observeDOM === true) {
    this.updatedObserver.observe(node, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['data-sku', 'data-identifier', 'data-set-id'],
      subtree: false,
      childList: false,
    })
  }
}

WidgetLoader.prototype.findContainer = function(node) {
  var parentNode = node.parentNode
  while (parentNode) {
    if (parentNode.nodeName.toLowerCase() === 'body') {
      return undefined
    }

    if (parentNode.getAttribute('data-8select-widget-id')) {
      return undefined
    }

    var isVangraafContainer = parentNode.classList.contains('section') && parentNode.classList.contains('outfit')
    if (parentNode.classList.contains('-eightselect-widget-container') || isVangraafContainer) {
      return parentNode
    }
    parentNode = parentNode.parentNode
  }
  return undefined
}

WidgetLoader.prototype.isDiscovered = function(node) {
  return (
    node.hasAttribute('data-8select-widget-state') && node.getAttribute('data-8select-widget-state') !== 'destroyed'
  )
}

WidgetLoader.prototype.load = function() {
  pub('sdk:initCSE:start', {})

  var widgetNodes = this.window.document.querySelectorAll('[data-8select-widget-id]')

  if (this.window._8select.config.featureFlags.observeDOM === true) {
    logger('featureFlag observeDOM enabled')

    const handleMutations = function(mutations) {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.hasAttribute('data-8select-widget-id')) {
              window._8select.utils.widgetLoader.createWidget(node)
            } else {
              const widgets = node.querySelectorAll('[data-8select-widget-id]')
              widgets.forEach(widget => {
                window._8select.utils.widgetLoader.createWidget(widget)
              })
            }
          }
        })
      })
    }

    const observer = new MutationObserver(handleMutations)
    const config = { attributes: false, childList: true, subtree: true }
    observer.observe(this.window.document, config)
  }

  var length = widgetNodes.length
  // sort widgets -> sys-button should be created first to avoid timing problems
  // -> app:content:found triggered before sys-button actually created
  for (var i = 0; i < length; i++) {
    var node = widgetNodes[i]
    this.createWidget(node)
  }

  pub('sdk:initCSE:end', {})
}

WidgetLoader.prototype.show = function(widgetUuid) {
  var instance = this.find(widgetUuid)
  if (instance) {
    ;(instance.container || instance.node).style.display = 'block'
  }
}

WidgetLoader.prototype.destroy = function(widgetUuid) {
  try {
    if (window._eightselect_shop_plugin && typeof window._eightselect_shop_plugin.hideSys === 'function') {
      window._eightselect_shop_plugin.hideSys()
    }

    var instance = this.find(widgetUuid)
    if (instance) {
      instance.destroy()
    }
  } catch (error) {
    logger('Error destroying widget', { widgetUuid, error })
  }
}

WidgetLoader.prototype.find = function(widgetUuid) {
  return this.widgetInstances[widgetUuid]
}
WidgetLoader.prototype.findWidgetByType = function(widgetType) {
  return Object.values(this.widgetInstances).find(widget => {
    return widget.type === widgetType
  })
}

WidgetLoader.prototype.getWidgetOffset = function(widgetUuid) {
  var instance = this.find(widgetUuid)
  if (!instance) {
    return 0
  }
  var instanceStyle = (instance.container || instance.node).style
  var displayBackup = instanceStyle.display

  instanceStyle.display = 'block'
  var top = instance.node.getBoundingClientRect().top
  instanceStyle.display = displayBackup

  return top
}

export default WidgetLoader
